import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Text14Medium, Text18Medium } from '../../mixins/mixins';
import Footer from '../Footer/Footer';
import Form from '../Form/Form';
import type { IModal } from './types';

const ModalComponent = styled.div`
  display: flex;
  justify-content: flex-end;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  min-height: 100vh;
  width: 100%;
  background: rgba(7, 24, 43, 0.4);
  backdrop-filter: blur(32px);
  z-index: 999999;
  overflow: auto;
`;

const ModalWrapper = styled.div<{ formIsVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 0 24px 24px;
  width: 60%;
  min-height: 100vh;
  height: fit-content;
  background: ${props =>
        props.formIsVisible ? 'rgba(7, 24, 43, 1)' : 'white'};
  @media (max-width: 669px) {
    min-height: 100%;
    width: 100%;
    padding: 0 12px 24px;
  }
`;

const CloseButton = styled.div<{ formIsVisible: boolean }>`
  ${Text14Medium};
  color: ${props =>
        props.formIsVisible
            ? 'rgba(255, 255, 255, 0.6)'
            : 'rgba(7, 24, 43, 0.6)'};
  margin-top: 30px;
  margin-right: 24px;
  margin-left: auto;
  cursor: pointer;
  @media (max-width: 669px) {
    margin-top: 18px;
    width: ${props => (props.formIsVisible ? 'unset' : '100%')};
    text-align: ${props => (props.formIsVisible ? 'unset' : 'center')};
  }
`;

const ButtonOpenForm = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 64px;
  margin-top: 30px;
  background: rgba(88, 255, 126, 1);
  border-radius: 64px;
  color: rgba(7, 24, 43, 1);
  cursor: pointer;
  ${Text18Medium};
  :hover {
    background: rgba(28, 99, 255, 1);
    color: white;
  }
  @media (max-width: 669px) {
    height: 40px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  @media (max-width: 669px) {
    margin-bottom: 16px;
  }
`;

const OrderLine = styled.div<{ formIsVisible: boolean | undefined }>`
  display: flex;
  width: 100%;
  height: 1px;
  background: ${props =>
        props.formIsVisible ? 'rgba(255, 255, 255, 0.2)' : 'rgba(7, 24, 43, 0.2)'};
  margin-bottom: 24px;
`;

const BackButton = styled.div<{ formIsVisible: boolean | undefined }>`
  display: ${props => (props.formIsVisible ? 'block' : 'none')};
  ${Text14Medium};
  margin-top: 30px;
  margin-right: auto;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  @media (max-width: 669px) {
    margin-top: 18px;
  }
`;

const Modal: React.FC<IModal> = ({ setIsVisible, status, setStatus, isVisible }) => {
    const isMobile = useIsMobile();
    const [formIsVisible, setFormIsVisible] = useState(false);

    const handleCloseModal = () => {
        setIsVisible(false);
        setFormIsVisible(false);
        setStatus('default');
    };

    const handleBackModal = () => {
        setFormIsVisible(false);
        setStatus('default');
    };

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.body.style.height = '100vh';
        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.height = 'unset';
        };
    }, []);

    return (
        <ModalComponent>
            <ModalWrapper formIsVisible={formIsVisible}>
                <ButtonWrapper>
                    <BackButton formIsVisible={formIsVisible} onClick={handleBackModal}>
                        [←] Back
                    </BackButton>
                    <CloseButton formIsVisible={formIsVisible} onClick={handleCloseModal}>
                        Close [X]
                    </CloseButton>
                </ButtonWrapper>
                {isMobile && <OrderLine formIsVisible={formIsVisible} />}
                {formIsVisible && (
                    <Form
                        formIsVisible={formIsVisible}
                        status={status}
                        setStatus={setStatus}
                        isVisible={isVisible}
                        isTop={true}
                    />
                )}
                {!formIsVisible && <Footer />}
                {!formIsVisible && (
                    <ButtonOpenForm onClick={() => setFormIsVisible(true)}>
                        Submit a request
                    </ButtonOpenForm>
                )}
            </ModalWrapper>
        </ModalComponent>
    );
};

export default Modal; 